import { Field } from "@engine/round/aggregate";
import { ReactNode } from "react";
import { FilteredProposalForm } from "server/routes/getProposal";

export type SpecialSectionType =
  | "longform"
  | "matching"
  | "for-profit"
  | "non-profit"
  | "funding"
  | null;

export interface Section {
  header: Field | null; // Section header field, null for fields before the first header
  fields: Field[]; // Fields within this section
  id: string; // Unique ID for state management (usually header.id or a generated one)
  longFormWarning?: ReactNode; // Optional warning message for long form sections
  specialSectionType: SpecialSectionType; // Tracks the type of special section
  indent?: boolean;
}

// -------------------- CONSTANTS --------------------

// Exposed so that other modules can reuse without duplicating.
export const INITIAL_SECTION_ID = "application-info-section";

export const alteredSectionHeaders: Record<string, string> = {
  "ORGANIZATION INFORMATION": "Organization Information",
  "RECEIVING CHARITY INFORMATION": "Receiving Charity Information",
  "COMPANY INFORMATION": "Company Information",
  "CORPORATE DOCUMENTS": "Corporate Documents",
  "STATE OF FINANCING": "State of Financing",
  "BASIC FUNDING REQUEST": "Basic Funding Request",
  "BASIC SPENDING PLAN (for base amount)":
    "Basic Spending Plan (for base amount)",
  "BASIC SPENDING PLAN (for basic funding request)":
    "Basic Spending Plan (for basic funding request)",
  "BOUNDS ON YOUR REQUEST": "Bounds on Your Request",
  "OTHER FUNDING": "Other Funding",
  "OTHER FUNDRAISING": "Other Fundraising",
  "MINIMUM AMOUNT": "Minimum Amount",
  "MAXIMUM AMOUNT": "Maximum Amount",
  "MAXIMUM REQUEST (optional)": "Maximum Request (optional)",
  "MATCHING PLEDGE REQUEST": "Matching Pledge Request",
  "AMBITIOUS REQUEST (optional)": "Ambitious Request (optional)",
  "LINKS OF INTEREST (optional)": "Links of Interest (optional)",
  "REQUIRED ACKNOWLEDGEMENTS": "Required Acknowledgements",
  "OPTIONAL DISCLOSURE PERMISSIONS FOR REVIEWERS":
    "Optional Disclosure Permissions for Reviewers",
  "THANK YOU!": "Thank You!",
  "BASE REQUEST": "Base Request",
};

export const sectionHeadersToSkip: string[] = [
  "Untitled Section",
  "Speculation Grant questions",
  "",
];

export const sectionGroupMapping: Record<string, string[]> = {
  "Non-Profit / Charity Application": [
    "Long-Form Application Attachment",
    "ORGANIZATION INFORMATION",
    "RECEIVING CHARITY INFORMATION",
  ],
  "For-Profit Company Application": [
    "Long-Form Application Attachment (for-profit version)",
    "COMPANY INFORMATION",
    "CORPORATE DOCUMENTS",
    "STATE OF FINANCING",
  ],
  "Funding Request": [
    "BASIC FUNDING REQUEST",
    "BASIC SPENDING PLAN (for basic funding request)",
    "MINIMUM AMOUNT",
    "AMBITIOUS REQUEST (optional)",
    "MAXIMUM AMOUNT",
    "Other Funding",
  ],
};

export const nonSectionResettingSectionHeaders = [
  "Long-Form Application Attachment",
  "ORGANIZATION INFORMATION",
  "RECEIVING CHARITY INFORMATION",
  "Long-Form Application Attachment (for-profit version)",
  "COMPANY INFORMATION",
  "CORPORATE DOCUMENTS",
  "STATE OF FINANCING",
  "BASIC FUNDING REQUEST",
  "BASIC SPENDING PLAN (for basic funding request)",
  "Other Funding",
  "MINIMUM AMOUNT",
  "MAXIMUM AMOUNT",
  "AMBITIOUS REQUEST (optional)",
];

// ----------------------------------------------------

export interface BuildSectionsOptions {
  filteredProposalForm: FilteredProposalForm;
  longformLink: string;
}

/**
 * Groups the Google-Form-derived fields into logical sections that the UI can
 * render. This codifies the somewhat gnarly Google Form business logic in a
 * single place so that the rendering component can stay cleaner.
 */
export function buildSections({
  filteredProposalForm,
  longformLink,
}: BuildSectionsOptions): Section[] {
  const groupedSections: Section[] = [];

  let currentSection: Section | null = null;
  let currentSpecialSectionType: SpecialSectionType = null;

  filteredProposalForm.fields.forEach((field, index) => {
    if (field.status !== "active") return;

    if (field.type === "sectionHeader") {
      if (sectionHeadersToSkip.includes(field.title)) return;

      let nextSpecialSectionType = currentSpecialSectionType;
      if (field.externalId === "longFormSectionHeader") {
        nextSpecialSectionType = "longform";
      } else if (!nonSectionResettingSectionHeaders.includes(field.title)) {
        switch (field.title) {
          case "MATCHING PLEDGE REQUEST":
            nextSpecialSectionType = "matching";
            break;
          case "For-Profit Company Application":
            nextSpecialSectionType = "for-profit";
            break;
          case "Non-Profit / Charity Application":
            nextSpecialSectionType = "non-profit";
            break;
          case "Funding Request":
            nextSpecialSectionType = "funding";
            break;
          default:
            nextSpecialSectionType = null;
            break;
        }
      }
      currentSpecialSectionType = nextSpecialSectionType;

      if (currentSection) groupedSections.push(currentSection);

      // Determine if this section should be indented
      const shouldIndent =
        nonSectionResettingSectionHeaders.includes(field.title) &&
        currentSpecialSectionType !== null;

      currentSection = {
        header: field,
        fields: [],
        id: field.id || `section-${index}`,
        specialSectionType: currentSpecialSectionType,
        indent: shouldIndent,
      };

      if (currentSpecialSectionType === "longform") {
        currentSection.longFormWarning = (
          <p
            key={`${field.id}-longformwarning`}
            className="text-sm text-red-500 my-2 ml-5"
          >
            The answers below are from the long form application and may not
            render fully. For the complete answer, please refer to the long form
            {longformLink && (
              <>
                {" "}
                <a
                  href={longformLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-500 underline hover:text-red-700"
                >
                  here
                </a>
              </>
            )}
            .
          </p>
        );
      }
    } else {
      // Non-header field
      if (!currentSection) {
        currentSection = {
          header: null,
          fields: [],
          id: INITIAL_SECTION_ID,
          specialSectionType: null,
          indent: false, // Initial section is never indented
        };
      }
      currentSection.fields.push(field);
    }
  });

  if (currentSection) groupedSections.push(currentSection);

  return groupedSections;
}
